package com.siriusxm.pia.views.unifiedaggregator.types

import androidx.compose.runtime.*
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.smithy4kt.SmithyShape
import unifiedcontent.aggregator.EntityTypeConfiguration
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.rgb
import org.jetbrains.compose.web.dom.*
import kotlin.time.Duration.Companion.seconds

/**
 * A panel that allows users to paste or edit JSON and validate it against the Smithy schema
 * definition for a specific entity type. Provides real-time feedback on validation errors
 * with line-level highlighting and JSON pointer references.
 *
 * @param aggregator The aggregator service providing access to entity models and Smithy schemas
 * @param entityType The entity type configuration to validate against
 * @param showJson Whether to display the JSON editor (set to false when JSON is provided externally)
 * @param initialJson Pre-populated JSON content to validate
 * @param message Description text shown above the JSON editor
 * @param panelContainer Custom container composable for wrapping the panel content
 * @param defaultMode Initial display mode (Edit for textarea, Preview for formatted view)
 * @param initialPreviewJson JSON to display in preview mode on initial render
 * @param resetExampleJson JSON to restore when the user clicks the Reset button
 */
@Composable
fun EntityTypeValidationPanel(
    aggregator: AggregatorService,
    entityType: EntityTypeConfiguration,
    showJson: Boolean = true,
    initialJson: String? = null,
    message: String = "Enter JSON for the entity to validate against the api-registry model.",
    panelContainer: @Composable (@Composable () -> Unit) -> Unit = { content ->
        box({
        }) {
            content()
        }
    },
    defaultMode: ValidationPanelMode = ValidationPanelMode.Edit,
    initialPreviewJson: String? = null,
    resetExampleJson: String? = null,
    extraActions: (@Composable (currentJson: String) -> Unit)? = null
) {
    Style(ValidationPanelStyles)

    val model = aggregator.entityModel
    val shapeId = entityType.shapeId
    val shape = remember(model, shapeId) {
        shapeId?.let { id ->
            model?.getShape(id)
        }
    }

    val validationKey = "${entityType.type}:${entityType.version}"
    val validationJsonInputState = remember(validationKey, initialJson) {
        mutableStateOf(initialJson.orEmpty())
    }
    val validationResultState = remember(validationKey, initialJson) {
        mutableStateOf<ValidationResult>(ValidationResult.Idle)
    }

    panelContainer {
        if (shape == null) {
            messageBox(MessageType.WARNING) {
                Text("Smithy metadata is unavailable for this type right now. Reload once the model is ready.")
            }
        } else {
            key(validationKey) {
                ValidationPanelContent(
                    shape = shape,
                    placeholder = """{ "id": "entity-id", "type": "${entityType.type}" }""",
                    description = message,
                    successMessage = "JSON strictly matches the ${entityType.name} specification",
                    jsonInputState = validationJsonInputState,
                    validationResultState = validationResultState,
                    showJson = showJson,
                    defaultMode = defaultMode,
                    initialPreviewJson = initialPreviewJson,
                    initialResetJson = resetExampleJson ?: initialJson,
                    entityType = entityType,
                    extraActions = extraActions
                )
            }
        }
    }
}

/**
 * Core validation panel content that renders the JSON editor, action buttons, and validation results.
 * Supports two display modes: Edit (textarea for input) and Preview (formatted JSON with line numbers).
 * Automatically detects CloudEvent envelopes and validates the inner data payload.
 *
 * @param shape Smithy shape definition to validate the JSON against
 * @param placeholder Placeholder text shown in the empty textarea
 * @param description Helper text displayed above the JSON editor
 * @param successMessage Message shown when validation passes
 * @param jsonInputState Mutable state holding the current JSON input
 * @param validationResultState Mutable state holding the latest validation result
 * @param showJson Whether to display the JSON editor area
 * @param defaultMode Initial display mode (Edit or Preview)
 * @param initialPreviewJson JSON to show in preview mode on first render
 * @param initialResetJson JSON to restore when the Reset button is clicked
 * @param entityType Optional entity type configuration used to validate type and version field values
 */
@Composable
fun ValidationPanelContent(
    shape: SmithyShape,
    placeholder: String,
    description: String,
    successMessage: String,
    jsonInputState: MutableState<String>,
    validationResultState: MutableState<ValidationResult>,
    showJson: Boolean = true,
    defaultMode: ValidationPanelMode = ValidationPanelMode.Edit,
    initialPreviewJson: String? = null,
    initialResetJson: String? = null,
    entityType: EntityTypeConfiguration? = null,
    extraActions: (@Composable (currentJson: String) -> Unit)? = null
) {
    var jsonInput by jsonInputState
    var resultState by validationResultState
    var validating by remember { mutableStateOf(false) }
    var copied by remember { mutableStateOf(false) }
    var lastValidatedJson by remember(initialPreviewJson) { mutableStateOf(initialPreviewJson) }
    var mode by remember(defaultMode) { mutableStateOf(defaultMode) }
    var inlineError by remember { mutableStateOf<String?>(null) }
    var showReset by remember { mutableStateOf(false) }
    var resultElement by remember { mutableStateOf<org.w3c.dom.Element?>(null) }

    LaunchedEffect(resultState) {
        if (resultState != ValidationResult.Idle) {
            resultElement?.scrollIntoView(SCROLL_SMOOTH_NEAREST)
        }
    }

    val validationIssues = (resultState as? ValidationResult.Error)?.issues.orEmpty()
    val previewReadyJson = remember(mode, jsonInput, lastValidatedJson) {
        if (mode == ValidationPanelMode.Preview) {
            (lastValidatedJson ?: jsonInput).escapeControlChars()
        } else {
            jsonInput
        }
    }
    val highlightSourceJson = lastValidatedJson
    val issueHighlightContext = remember(highlightSourceJson, resultState) {
        if (validationIssues.isEmpty() || highlightSourceJson.isNullOrBlank()) {
            null
        } else {
            buildIssueHighlightContext(highlightSourceJson, validationIssues)
        }
    }
    val lineHighlights = issueHighlightContext?.lineHighlights.orEmpty()
    val highlightMatchesInput = issueHighlightContext?.rawJson == lastValidatedJson
    val metadata = resultState.metadata

    LaunchedEffect(copied) {
        if (copied) {
            delay(3.seconds)
            copied = false
        }
    }

    Column(gap = 12.px) {
        if (description.isNotBlank()) {
            Div {
                Text(description)
            }
        }

        metadata.validatedPointer?.takeIf { metadata.isCloudEventEnvelope }?.let { pointer ->
            Div({ classes(ValidationPanelStyles.validationNote) }) {
                Text("Detected CloudEvent envelope. Validating $pointer against the schema.")
            }
        }

        metadata.takeIf { it.isContentUpdateEnvelope }?.let {
            val updateType = it.contentUpdateType ?: "unknown"
            val pointer = it.validatedPointer ?: "entity"
            Div({ classes(ValidationPanelStyles.validationNote) }) {
                if (updateType == "REMOVE") {
                    Text("Detected $updateType content update. Only id, type, and version are required.")
                } else {
                    Text("Detected $updateType content update. Validating $pointer against the schema.")
                }
            }
        }

        if (showJson) {
            when (mode) {
                ValidationPanelMode.Edit -> {
                    TextArea(jsonInput) {
                        attr("placeholder", placeholder)
                        classes(ValidationPanelStyles.codeTextArea)
                        onInput {
                            jsonInput = it.value
                            inlineError = null
                            showReset = false
                        }
                    }
                }

                ValidationPanelMode.Preview -> {
                    val previewHighlights = if (highlightMatchesInput) lineHighlights else emptyMap()
                    JsonPreview(
                        prettyJson = previewReadyJson,
                        highlights = previewHighlights,
                        onRequestEdit = { mode = ValidationPanelMode.Edit }
                    )
                }
            }
        }

        Div({
            classes(ValidationPanelStyles.formActions)
        }) {
        button {
            title = "Validate"
            primary = true
            enabled = !validating
            action {
                validating = true
                inlineError = null
                try {
                    val payload = jsonInput
                    val result = validateAgainstSmithy(payload, shape, successMessage, entityType?.type, entityType?.version, entityType?.idPattern)
                    resultState = result
                    inlineError = when (result) {
                        is ValidationResult.Error -> when (result.title) {
                            "Invalid JSON", "JSON payload is required" ->
                                result.issues.firstOrNull()?.message ?: result.title

                            else -> null
                        }

                        else -> null
                    }
                    val hasBlockingInlineError = inlineError != null
                    when (result) {
                        is ValidationResult.Success -> {
                            val formattedPayload = payload.formatIfPossible()
                            jsonInput = formattedPayload
                            lastValidatedJson = formattedPayload
                            mode = ValidationPanelMode.Preview
                        }

                        is ValidationResult.Error -> {
                            if (hasBlockingInlineError) {
                                lastValidatedJson = null
                                mode = ValidationPanelMode.Edit
                            } else {
                                lastValidatedJson = payload
                                mode = ValidationPanelMode.Preview
                            }
                        }

                        ValidationResult.Idle -> {
                            lastValidatedJson = null
                            mode = ValidationPanelMode.Edit
                        }
                    }
                } finally {
                    validating = false
                }
            }
        }

        button {
            title = if (mode == ValidationPanelMode.Preview) "Edit" else "Format"
            enabled = if (mode == ValidationPanelMode.Preview) {
                true
            } else {
                jsonInput.isNotBlank() && !validating
            }
            action {
                if (mode == ValidationPanelMode.Preview) {
                    mode = ValidationPanelMode.Edit
                } else {
                    val formatted = jsonInput.tryFormatJson()
                    inlineError = formatted.error
                    formatted.prettyJson?.let {
                        jsonInput = it
                    }
                }
            }
        }

        button {
            title = "Copy"
            enabled = jsonInput.isNotBlank()
            action {
                window.navigator.clipboard.writeText(jsonInput)
                    .then { copied = true }
                    .catch { inlineError = "Unable to copy JSON to clipboard." }
            }
        }

        val hasInitialReset = !initialResetJson.isNullOrBlank()
        button {
            title = if (showReset && hasInitialReset) "Reset" else "Clear"
            enabled = if (showReset && hasInitialReset) {
                true
            } else {
                jsonInput.isNotBlank()
            }
            action {
                if (showReset && hasInitialReset) {
                    val sample = initialResetJson.orEmpty()
                    jsonInput = sample
                    resultState = ValidationResult.Idle
                    inlineError = null
                    lastValidatedJson = sample
                    mode = ValidationPanelMode.Preview
                    showReset = false
                } else {
                    jsonInput = ""
                    resultState = ValidationResult.Idle
                    inlineError = null
                    mode = ValidationPanelMode.Edit
                    lastValidatedJson = null
                    showReset = hasInitialReset
                }
            }
        }

        extraActions?.invoke(jsonInput)

        if (copied) {
            Span({ classes(ValidationPanelStyles.copyIndicator) }) {
                Text("Copied")
            }
        }

        if (validating) {
            spinner(size = Size.SMALL)
        }
    }

    inlineError?.let { errorMessage ->
        Div({ classes(ValidationPanelStyles.formResult) }) {
            messageBox(MessageType.ALERT) {
                Text(errorMessage)
            }
        }
    }

    Div({
        ref { element ->
            resultElement = element
            onDispose { resultElement = null }
        }
    }) {
        when (val state = resultState) {
            is ValidationResult.Success -> {
                Div({
                    classes(ValidationPanelStyles.formResult)
                }) {
                    messageBox(MessageType.SUCCESS) {
                        Text(state.message)
                    }
                }
            }

            is ValidationResult.Error -> {
                Div({
                    classes(ValidationPanelStyles.formResult)
                }) {
                    messageBox(MessageType.ALERT) {
                        Text(state.title)
                        if (state.issues.isNotEmpty()) {
                            Ul {
                                state.issues.forEach { issue ->
                                    Li {
                                        if (issue.pointer != null || issue.schemaContext != null) {
                                            Div({ classes(ValidationPanelStyles.issueHeader) }) {
                                                issue.pointer?.let { pointer ->
                                                    Span({ classes(ValidationPanelStyles.issuePointer) }) {
                                                        Text(pointer)
                                                    }
                                                    lineHighlights[pointer]?.let { range ->
                                                        Span({ classes(ValidationPanelStyles.issuePointerLine) }) {
                                                            Text(range.label())
                                                        }
                                                    }
                                                }
                                                issue.schemaContext?.let { context ->
                                                    Span({ classes(ValidationPanelStyles.issueSchema) }) {
                                                        Text(context)
                                                    }
                                                }
                                            }
                                        }
                                        Div({ classes(ValidationPanelStyles.issueMessage) }) {
                                            Text(issue.message)
                                        }
                                    }
                                }
                            }
                        }
                    }
                    if (issueHighlightContext != null && !highlightMatchesInput) {
                        Div({ classes(ValidationPanelStyles.validationNote) }) {
                            Text("Validation line references reflect the last payload you validated. Re-run validation to update them for the current JSON.")
                        }
                    }
                }
            }

            ValidationResult.Idle -> {}
        }
    }
    }
}

/**
 * Stylesheet for the JSON validation panel components including the textarea,
 * preview container, action buttons, and validation result displays.
 */
object ValidationPanelStyles : StyleSheet() {
    val codeTextArea by style {
        width(100.percent)
        minHeight(420.px)
        fontFamily("monospace")
        fontSize(0.95.cssRem)
        padding(1.em)
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, rgb(235, 235, 235))
        property("resize", "vertical")
    }

    val formActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
    }

    val formResult by style {
    }

    val validationNote by style {
        fontSize(0.85.cssRem)
        color(rgb(90, 90, 90))
    }

    val copyIndicator by style {
        fontSize(0.85.cssRem)
        color(rgb(90, 90, 90))
    }

    val issueHeader by style {
        display(DisplayStyle.Flex)
        gap(10.px)
        alignItems(AlignItems.Center)
        marginBottom(4.px)
    }

    val issuePointer by style {
        fontFamily("monospace")
        backgroundColor(rgb(237, 242, 255))
        padding(2.px, 6.px)
        borderRadius(4.px)
        fontSize(0.85.cssRem)
    }

    val issuePointerLine by style {
        fontSize(0.8.cssRem)
        color(rgb(120, 120, 120))
    }

    val issueSchema by style {
        fontSize(0.8.cssRem)
        color(rgb(120, 120, 120))
        fontStyle("italic")
    }

    val issueMessage by style {
        fontSize(0.95.cssRem)
    }

    val previewContainer by style {
        padding(1.em)
        borderRadius(6.px)
        backgroundColor(rgb(250, 250, 250))
        fontFamily("monospace")
        fontSize(0.95.cssRem)
        maxHeight(420.px)
        overflowY("auto")
        border(1.px, LineStyle.Solid, rgb(235, 235, 235))
        cursor("text")
    }

    val previewRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Stretch)
        gap(0.75.em)
        whiteSpace("pre")
    }

    val previewRowHighlight by style {
        backgroundColor(rgb(255, 248, 230))
    }

    val previewLineNumber by style {
        minWidth(3.5.em)
        textAlign("right")
        color(rgb(150, 150, 150))
        property("user-select", "none")
    }

    val previewLineContent by style {
        flexGrow(1)
        whiteSpace("pre")
    }
}

/**
 * Display mode for the validation panel.
 * - Edit: Shows a textarea for entering/editing JSON
 * - Preview: Shows formatted JSON with line numbers and error highlighting
 */
enum class ValidationPanelMode { Edit, Preview }

/**
 * Renders a read-only formatted JSON view with line numbers and optional error highlighting.
 * Clicking anywhere on the preview switches to edit mode. Lines with validation errors
 * are highlighted to help users locate issues in their JSON payload.
 *
 * @param prettyJson The formatted JSON string to display
 * @param highlights Map of JSON pointers to line ranges that should be highlighted as errors
 * @param onRequestEdit Callback invoked when the user clicks to switch to edit mode
 */
@Composable
private fun JsonPreview(
    prettyJson: String,
    highlights: Map<String, LineHighlight>,
    onRequestEdit: () -> Unit
) {
    val lines = remember(prettyJson) { prettyJson.split('\n') }
    val highlightedLines = remember(highlights) {
        highlights.values.flatMap { range -> (range.startLine..range.endLine).toList() }.toSet()
    }

    Div({
        classes(ValidationPanelStyles.previewContainer)
        attr("tabindex", "0")
        attr("aria-label", "JSON preview. Click to edit.")
        onClick {
            onRequestEdit()
        }
        onKeyDown {
            if (it.key == "Escape") {
                onRequestEdit()
            }
        }
    }) {
        lines.forEachIndexed { index, line ->
            val isHighlighted = highlightedLines.contains(index + 1)
            Div({
                classes(ValidationPanelStyles.previewRow)
                if (isHighlighted) {
                    classes(ValidationPanelStyles.previewRowHighlight)
                }
            }) {
                Div({ classes(ValidationPanelStyles.previewLineNumber) }) {
                    Text((index + 1).toString())
                }
                Div({ classes(ValidationPanelStyles.previewLineContent) }) {
                    Text(line.ifEmpty { " " })
                }
            }
        }
    }
}

/**
 * Escapes control characters within JSON string values for safe display.
 * Converts newlines, tabs, and other control characters to their escape sequences.
 */
private fun String.escapeControlChars(): String {
    if (isEmpty()) {
        return this
    }
    val builder = StringBuilder(length + 8)
    var inString = false
    var escapeNext = false
    for (ch in this) {
        when {
            escapeNext -> {
                builder.append(ch)
                escapeNext = false
            }

            ch == '\\' && inString -> {
                builder.append(ch)
                escapeNext = true
            }

            ch == '"' -> {
                builder.append(ch)
                inString = !inString
            }

            inString -> when (ch) {
                '\n' -> builder.append("\\n")
                '\r' -> builder.append("\\r")
                '\t' -> builder.append("\\t")
                '\b' -> builder.append("\\b")
                '\u000C' -> builder.append("\\f")
                '\u2028' -> builder.append("\\u2028")
                '\u2029' -> builder.append("\\u2029")
                else -> if (ch < ' ') {
                    builder.append("\\u")
                    builder.append(ch.code.toString(16).padStart(4, '0'))
                } else {
                    builder.append(ch)
                }
            }

            else -> builder.append(ch)
        }
    }
    return builder.toString()
}
